import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

/* ─── Inline styles (scoped, no global leakage) ──────────────────────────── */
const S = {
  section: {
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    background: 'linear-gradient(135deg, #f0f8fc 0%, #e8f4f8 50%, #f5fbfd 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  bgCircle1: {
    position: 'absolute',
    top: '-120px',
    right: '-120px',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(0,168,204,0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  bgCircle2: {
    position: 'absolute',
    bottom: '-80px',
    left: '-80px',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(29,106,136,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    zIndex: 1,
    background: '#ffffff',
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0,37,54,0.10), 0 4px 16px rgba(0,37,54,0.06)',
    padding: '60px 50px',
    maxWidth: '580px',
    width: '100%',
    textAlign: 'center',
    animation: 'ty-fadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) both',
  },
  iconWrap: {
    width: '96px',
    height: '96px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #00a8cc 0%, #1d6a88 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 32px',
    boxShadow: '0 8px 32px rgba(0,168,204,0.30)',
    animation: 'ty-pop 0.5s 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both',
  },
  checkSvg: {
    width: '44px',
    height: '44px',
    color: '#ffffff',
    strokeDasharray: 100,
    strokeDashoffset: 100,
    animation: 'ty-draw 0.6s 0.5s ease forwards',
  },
  eyebrow: {
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: '#00a8cc',
    marginBottom: '12px',
  },
  heading: {
    fontSize: '34px',
    fontWeight: '800',
    color: '#002536',
    lineHeight: 1.2,
    marginBottom: '14px',
  },
  subtext: {
    fontSize: '15px',
    color: '#5a7a8a',
    lineHeight: 1.7,
    marginBottom: '36px',
  },
  infoBox: {
    background: '#f5fbfd',
    border: '1px solid #d0eaf3',
    borderRadius: '14px',
    padding: '24px 28px',
    marginBottom: '36px',
    textAlign: 'left',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #e6f2f8',
    fontSize: '14px',
  },
  infoRowLast: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    fontSize: '14px',
  },
  infoLabel: {
    color: '#7a9aaa',
    fontWeight: '500',
  },
  infoValue: {
    color: '#002536',
    fontWeight: '700',
    textAlign: 'right',
    maxWidth: '60%',
    wordBreak: 'break-word',
  },
  amountValue: {
    color: '#00a8cc',
    fontWeight: '800',
    fontSize: '18px',
  },
  btnGroup: {
    display: 'flex',
    gap: '14px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'linear-gradient(135deg, #00a8cc 0%, #1d6a88 100%)',
    color: '#fff',
    padding: '14px 28px',
    borderRadius: '50px',
    fontWeight: '700',
    fontSize: '14px',
    textDecoration: 'none',
    boxShadow: '0 4px 16px rgba(0,168,204,0.30)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    border: 'none',
    cursor: 'pointer',
  },
  btnSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'transparent',
    color: '#002536',
    padding: '13px 28px',
    borderRadius: '50px',
    fontWeight: '700',
    fontSize: '14px',
    textDecoration: 'none',
    border: '2px solid #d0e8f0',
    transition: 'border-color 0.2s, color 0.2s',
  },
  emailNote: {
    marginTop: '28px',
    fontSize: '13px',
    color: '#8aa5b0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
};

const keyframes = `
@keyframes ty-fadeUp {
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes ty-pop {
  from { opacity: 0; transform: scale(0.5); }
  to   { opacity: 1; transform: scale(1); }
}
@keyframes ty-draw {
  to { stroke-dashoffset: 0; }
}
`;

export default function PaymentThankYou() {
  const { state } = useLocation();
  const navigate = useNavigate();

  // Redirect if someone lands here directly without payment state
  useEffect(() => {
    if (!state || !state.orderNumber) {
      navigate('/', { replace: true });
    }
  }, [state, navigate]);

  if (!state || !state.orderNumber) return null;

  const { orderNumber, customerName, customerEmail, packagePrice, serviceSelected } = state;

  return (
    <>
      <style>{keyframes}</style>
      <Header />

      <section style={S.section}>
        <div style={S.bgCircle1} />
        <div style={S.bgCircle2} />

        <div style={S.card}>
          {/* Animated check icon */}
          <div style={S.iconWrap}>
            <svg
              style={S.checkSvg}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <p style={S.eyebrow}>Payment Successful</p>
          <h1 style={S.heading}>Thank You, {customerName.split(' ')[0]}!</h1>
          <p style={S.subtext}>
            Your payment has been received and your order is now being processed.
            A confirmation has been sent to your email.
          </p>

          {/* Order info box */}
          <div style={S.infoBox}>
            <div style={S.infoRow}>
              <span style={S.infoLabel}>Order Number</span>
              <span style={S.infoValue}>{orderNumber}</span>
            </div>
            <div style={S.infoRow}>
              <span style={S.infoLabel}>Service</span>
              <span style={S.infoValue}>{serviceSelected}</span>
            </div>
            <div style={S.infoRow}>
              <span style={S.infoLabel}>Email</span>
              <span style={S.infoValue}>{customerEmail}</span>
            </div>
            <div style={S.infoRowLast}>
              <span style={S.infoLabel}>Amount Paid</span>
              <span style={{ ...S.infoValue, ...S.amountValue }}>
                ${Number(packagePrice).toFixed(2)}
              </span>
            </div>
          </div>

          {/* Buttons */}
          <div style={S.btnGroup}>
            <Link to="/" style={S.btnPrimary}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              Back to Home
            </Link>
            <Link to="/user-dashboard" style={S.btnSecondary}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
              Go to Dashboard
            </Link>
          </div>

          <p style={S.emailNote}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            Confirmation email sent to {customerEmail}
          </p>
        </div>
      </section>

      <Footer />
    </>
  );
}